import React, { useState, useEffect } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Trash2, Plus, Shield, Ban } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

export default function BackofficeSettings() {
  const [dnc, setDnc] = useState([]);
  const [dncEmail, setDncEmail] = useState('');
  const [dncReason, setDncReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingConfig, setSendingConfig] = useState(null);

  const loadDnc = () => {
    setLoading(true);
    api.get('/backoffice/dnc')
      .then(r => setDnc(r.data.dnc_list || []))
      .catch(() => toast.error('Failed to load DNC'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDnc();
    api.get('/backoffice/sending-config').then(r => setSendingConfig(r.data)).catch(() => {});
  }, []);

  const addDnc = async () => {
    try {
      await api.post('/backoffice/dnc', { email: dncEmail, reason: dncReason || 'Manual add' });
      toast.success('Added to DNC');
      setDncEmail('');
      setDncReason('');
      loadDnc();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const removeDnc = async (id) => {
    await api.delete(`/backoffice/dnc/${id}`);
    toast.success('Removed');
    loadDnc();
  };

  return (
    <BackofficeLayout>
      <div data-testid="backoffice-settings">
        <h1 className="text-xl font-bold text-white tracking-tight mb-6" style={{ fontFamily: 'Barlow Condensed' }}>SETTINGS</h1>

        <div className="grid gap-4 max-w-2xl">
          {/* Feature Flags */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Shield className="w-4 h-4" /> Feature Flags
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm text-slate-200">Outbound Sending</p>
                  <p className="text-xs text-slate-500">Enable actual email sending via Resend</p>
                </div>
                <Badge variant="outline" className={`text-[10px] ${
                  sendingConfig?.outbound_sending_enabled
                    ? 'text-green-400 border-green-500/30'
                    : 'text-red-400 border-red-500/30'
                }`} data-testid="sending-status-badge">
                  {sendingConfig?.outbound_sending_enabled ? 'ENABLED' : 'DISABLED'}
                </Badge>
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm text-slate-200">Email Provider</p>
                  <p className="text-xs text-slate-500">{sendingConfig?.resend_configured ? `Resend (${sendingConfig.from_email})` : 'Mock provider'}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] ${
                  sendingConfig?.resend_configured
                    ? 'text-green-400 border-green-500/30'
                    : 'text-amber-400 border-amber-500/30'
                }`}>
                  {sendingConfig?.resend_configured ? 'RESEND' : 'MOCK'}
                </Badge>
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm text-slate-200">Daily Cap</p>
                  <p className="text-xs text-slate-500">Max emails per day</p>
                </div>
                <span className="text-xs font-mono text-slate-300" data-testid="daily-cap">{sendingConfig?.daily_cap ?? 25}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm text-slate-200">Domain Cap</p>
                  <p className="text-xs text-slate-500">Max per recipient domain per day</p>
                </div>
                <span className="text-xs font-mono text-slate-300" data-testid="domain-cap">{sendingConfig?.domain_cap ?? 5}</span>
              </div>
            </CardContent>
          </Card>

          {/* DNC List */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Ban className="w-4 h-4" /> Do Not Contact List <span className="text-slate-600 font-mono">({dnc.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input
                  type="email"
                  placeholder="Email address"
                  className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm flex-1"
                  value={dncEmail}
                  onChange={e => setDncEmail(e.target.value)}
                  data-testid="dnc-email-input"
                />
                <Input
                  placeholder="Reason"
                  className="bg-slate-800 border-slate-700 text-slate-200 h-8 rounded-sm flex-1"
                  value={dncReason}
                  onChange={e => setDncReason(e.target.value)}
                  data-testid="dnc-reason-input"
                />
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm h-8" onClick={addDnc} disabled={!dncEmail} data-testid="add-dnc-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {loading ? (
                <div className="h-16 animate-pulse bg-slate-800 rounded-sm" />
              ) : dnc.length === 0 ? (
                <p className="text-xs text-slate-600 text-center py-4">DNC list is empty</p>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {dnc.map(item => (
                    <div key={item.id} className="flex items-center justify-between px-3 py-2 bg-slate-800 rounded-sm text-sm">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-slate-200 font-mono text-xs">{item.email}</span>
                        <Badge variant="outline" className={`text-[10px] ${
                          item.reason === 'bounce' ? 'text-red-400 border-red-500/30' :
                          item.reason === 'complaint' ? 'text-red-400 border-red-500/30' :
                          item.reason === 'unsubscribe' ? 'text-amber-400 border-amber-500/30' :
                          'text-slate-400 border-slate-600'
                        }`}>{item.reason || 'manual'}</Badge>
                        {item.source && item.source !== 'admin' && (
                          <span className="text-[10px] text-slate-600">{item.source}</span>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" className="text-slate-500 hover:text-red-400 h-6 w-6 p-0" onClick={() => removeDnc(item.id)} data-testid={`remove-dnc-${item.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </BackofficeLayout>
  );
}
